/**
 * MCP bridge status store — persisted connection settings and live
 * connection state, read by the Settings pane and the StatusBar badge.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MCP_BRIDGE_SETTINGS_KEY, MCP_DEFAULT_PORT } from './mcpBridgeProtocol';

export type McpConnectionState = 'disabled' | 'connecting' | 'connected' | 'offline';

interface McpBridgeState {
    /** Master switch: when off the client never contacts the relay. */
    enabled: boolean;
    /** Local relay port (the Node MCP server's HTTP bridge endpoint). */
    port: number;
    /** When true, mutating tools require an in-app approval click. */
    requireApproval: boolean;

    // --- live state (not persisted) ---
    connection: McpConnectionState;
    lastError: string | null;
    /** Requests served since the last connect (for the settings readout). */
    servedCount: number;
    lastRequestAt: number | null;

    setEnabled: (enabled: boolean) => void;
    setPort: (port: number) => void;
    setRequireApproval: (value: boolean) => void;
    setConnection: (connection: McpConnectionState, lastError?: string | null) => void;
    recordServed: () => void;
}

export const useMcpBridgeStore = create<McpBridgeState>()(
    persist(
        (set) => ({
            enabled: true,
            port: MCP_DEFAULT_PORT,
            requireApproval: false,

            connection: 'disabled',
            lastError: null,
            servedCount: 0,
            lastRequestAt: null,

            setEnabled: (enabled) => set({ enabled, connection: enabled ? 'connecting' : 'disabled', lastError: null }),
            setPort: (port) => set({ port: Math.min(Math.max(Math.round(port) || MCP_DEFAULT_PORT, 1024), 65535) }),
            setRequireApproval: (requireApproval) => set({ requireApproval }),
            setConnection: (connection, lastError = null) => set({ connection, lastError }),
            recordServed: () => set((s) => ({ servedCount: s.servedCount + 1, lastRequestAt: Date.now() })),
        }),
        {
            name: MCP_BRIDGE_SETTINGS_KEY,
            version: 2,
            migrate: (persisted, fromVersion) => {
                const saved = (persisted ?? {}) as Partial<Pick<McpBridgeState, 'enabled' | 'port' | 'requireApproval'>>;
                // v0 persisted the retired relay default (8721 — Pik's app link now);
                // re-point exactly that retired value, a deliberate custom port stays.
                const port = fromVersion < 1 && saved.port === 8721 ? MCP_DEFAULT_PORT : saved.port;
                // v1 persisted `enabled: false` on every status write, so a stored
                // false is usually the old default, not a deliberate opt-out —
                // a pre-v2 state wakes up enabled; a later manual switch-off sticks.
                return { ...saved, port, enabled: true } as McpBridgeState;
            },
            partialize: (state) => ({
                enabled: state.enabled,
                port: state.port,
                requireApproval: state.requireApproval,
            }),
        },
    ),
);
